/**
 * <HostFinancialsTab> - Iter 9.40 Pass H
 *
 * Host-side Insights > Financials. Shows tracked revenue + optional projections toggle.
 * Backend: GET /api/host/insights/financials?include_projections=bool
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { DollarSign, TrendingUp, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const PIPELINE_ORDER = ["invited", "quoted", "booked_deposit_pending", "confirmed", "completed", "cancelled"];

export default function HostFinancialsTab() {
  const [data, setData] = useState(null);
  const [includeProjections, setIncludeProjections] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem("session_token");
    fetch(`${API}/host/insights/financials?include_projections=${includeProjections}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [includeProjections]);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  if (!data) return <div className="py-10 text-center text-sm text-gray-500">Failed to load financials.</div>;

  return (
    <div className="space-y-5" data-testid="host-financials-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <DollarSign className="w-5 h-5" /> Financials
          </h2>
          <p className="text-sm text-gray-500">Revenue you've booked and your pipeline value.</p>
        </div>
        <div className="flex items-center gap-2" data-testid="projections-toggle">
          <Label className="text-sm">Projections</Label>
          <Switch checked={includeProjections} onCheckedChange={setIncludeProjections} />
        </div>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card data-testid="fin-booked">
          <CardContent className="p-4">
            <div className="text-xs uppercase font-medium text-gray-500">Booked</div>
            <div className="text-3xl font-bold mt-2">{fmt(data.total_booked_revenue_usd)}</div>
          </CardContent>
        </Card>
        <Card data-testid="fin-pipeline">
          <CardContent className="p-4">
            <div className="text-xs uppercase font-medium text-gray-500">Pipeline</div>
            <div className="text-3xl font-bold mt-2">{fmt(data.pipeline_value_usd)}</div>
          </CardContent>
        </Card>
        {includeProjections && (
          <Card data-testid="fin-projected">
            <CardContent className="p-4">
              <div className="text-xs uppercase font-medium text-gray-500 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" /> Projected 30d
              </div>
              <div className="text-3xl font-bold mt-2 text-purple-700">{fmt(data.projected_next_30d_usd)}</div>
              <p className="text-[10px] text-gray-500 mt-1">{data.projection_assumptions}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pipeline kanban */}
      <div data-testid="fin-kanban">
        <h3 className="text-base font-semibold mb-3">Pipeline</h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {PIPELINE_ORDER.map((status) => {
            const bucket = (data.by_status || {})[status] || { count: 0, value: 0 };
            return (
              <Card key={status} data-testid={`fin-kanban-${status}`}>
                <CardContent className="p-3">
                  <div className="text-[10px] uppercase font-medium text-gray-500">{status.replace(/_/g, " ")}</div>
                  <div className="text-lg font-bold mt-1">{bucket.count}</div>
                  <div className="text-[11px] text-gray-600">{fmt(bucket.value)}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Per-category */}
      <div data-testid="fin-by-category">
        <h3 className="text-base font-semibold mb-3">By category</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {Object.entries(data.by_category || {}).map(([cat, b]) => (
            <Card key={cat} data-testid={`fin-cat-${cat}`}>
              <CardContent className="p-3">
                <div className="font-medium text-sm capitalize">{cat}</div>
                <div className="flex items-center justify-between text-xs text-gray-600 mt-1">
                  <span>{b.count} experiences</span>
                  <Badge variant="secondary">{fmt(b.value)}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
